import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, Sparkles, MapPin, Compass, Star, Clock, Globe, Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "@/styles/Index.css";

const Index = () => {
  return (
    <div className="min-h-screen w-full overflow-hidden bg-background">
      <Header />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-4 py-20 mt-16">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100/10 via-pink-50/10 to-blue-50/10 dark:from-purple-900/10 dark:via-pink-900/10 dark:to-blue-900/10 -z-10" />
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 dark:bg-[url('/images/globe_light.png')] bg-[url('/images/globe_dark.png')] bg-left bg-no-repeat bg-contain opacity-40 z-0" />
        </div>
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left space-y-8 animate-slide-up">
              <div className="hidden md:inline-flex items-center gap-2 dark:bg-white/10 bg-white/70 backdrop-blur-sm border border-white/20 text-primary px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all">
                <MapPin className="h-5 w-5" />
                <span className="text-sm font-medium dark:text-white">AI-Powered City Guides</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold tracking-tight bg-gradient-to-r from-primary via-purple-600 to-pink-500 text-transparent bg-clip-text hero-message md:pb-4">
                Discover the best places around the city with
                <span className="magic">
                  <span className="magic-text md:ml-2">AI-generated</span>
                </span>
                guides, in seconds
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed">
                Experience local highlights, hidden gems, and custom itineraries powered by artificial intelligence.
              </p>
              <div className="flex items-start gap-4 pt-8">
                <Button size="lg" className="h-14 px-8 bg-gradient-to-r from-primary to-purple-600 hover:opacity-90 transition-opacity">
                  Get Started Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="h-14 px-8 border-2">
                  View Sample Guides
                  <Search className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="relative z-10 animate-slide-up">
                <img
                  src="https://placehold.co/600x800/9333ea/ffffff?text=App+Screenshot+1"
                  alt="Ulixes App Interface"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 z-0 animate-fade-in">
                <img
                  src="https://placehold.co/400x600/9333ea/ffffff?text=App+Screenshot+2"
                  alt="Ulixes App Features"
                  className="rounded-2xl shadow-2xl opacity-60"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid with Images */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-purple-600 text-transparent bg-clip-text">
              Why Choose Ulixes App
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Experience the future of travel planning with our AI-powered city guides
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="relative">
              <img
                src="https://placehold.co/600x400/9333ea/ffffff?text=Custom+Itineraries"
                alt="Custom Itineraries Feature"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/20 rounded-full blur-2xl -z-10" />
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-bold">Custom Itineraries</h3>
              <p className="text-muted-foreground">
                Let AI create the perfect itinerary based on your interests, time, and budget. Get personalized recommendations for attractions, restaurants, and activities.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            <div className="space-y-6 order-2 lg:order-1">
              <h3 className="text-2xl font-bold">Local Insights</h3>
              <p className="text-muted-foreground">
                Discover hidden gems and local favorites that you won't find in traditional travel guides. Our AI analyzes thousands of local recommendations.
              </p>
            </div>
            <div className="relative order-1 lg:order-2">
              <img
                src="https://placehold.co/600x400/9333ea/ffffff?text=Local+Insights"
                alt="Local Insights Feature"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/20 rounded-full blur-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-b from-background to-purple-50/10 dark:to-purple-900/10">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard number="193" label="Countries" />
            <StatCard number="10000+" label="Cities" />
            <StatCard number="50k+" label="Guides Generated" />
            <StatCard number="24/7" label="AI Assistance" />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-purple-600 text-transparent bg-clip-text">
              Why Choose Ulixes App
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Experience the future of travel planning with our AI-powered city guides
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Clock className="h-6 w-6 text-primary" />}
              title="Instant Guides"
              description="Get personalized city guides in seconds, not hours of research."
            />
            <FeatureCard
              icon={<Star className="h-6 w-6 text-purple-600" />}
              title="Local Insights"
              description="Discover hidden gems and local favorites other tourists might miss."
            />
            <FeatureCard
              icon={<Compass className="h-6 w-6 text-pink-500" />}
              title="Custom Routes"
              description="Get optimized routes based on your interests and available time."
            />
            <FeatureCard
              icon={<Globe className="h-6 w-6 text-blue-500" />}
              title="Global Coverage"
              description="Access guides for hundreds of cities around the world."
            />
            <FeatureCard
              icon={<Sparkles className="h-6 w-6 text-green-500" />}
              title="Smart Recommendations"
              description="AI-powered suggestions based on your preferences and travel style."
            />
            <FeatureCard
              icon={<MapPin className="h-6 w-6 text-orange-500" />}
              title="Offline Access"
              description="Download guides for offline use during your travels."
            />
          </div>
        </div>
      </section>

      {/* How It Works with Images */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-50/10 via-background to-pink-50/10 dark:from-purple-900/10 dark:via-background dark:to-pink-900/10">
        <div className="container mx-auto max-w-6xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 bg-gradient-to-r from-primary to-purple-600 text-transparent bg-clip-text">
            How It Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative mb-12">
            <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gradient-to-r from-primary to-purple-600 -z-10 hidden md:block" />
            <StepCard
              number="01"
              title="Choose Your City"
              description="Select your destination and tell us about your interests and preferences."
            />
            <StepCard
              number="02"
              title="AI Creates Guide"
              description="Our AI generates a personalized guide with recommendations just for you."
            />
            <StepCard
              number="03"
              title="Start Exploring"
              description="Access your guide instantly and start discovering amazing places."
            />
          </div>
          <div className="mt-12">
            <img
              src="https://placehold.co/1200x600/9333ea/ffffff?text=App+Interface+Demo"
              alt="Ulixes App Interface Demo"
              className="rounded-2xl shadow-2xl mx-auto"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          <Card className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-purple-400/20 to-pink-400/20 backdrop-blur-xl" />
            <div className="relative p-12 text-center">
              <h2 className="text-3xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-primary to-purple-600 text-transparent bg-clip-text">
                Ready to Explore?
              </h2>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join thousands of travelers who are discovering cities smarter with AI-powered guides.
              </p>
              <Button size="lg" className="h-14 px-8 bg-gradient-to-r from-primary to-purple-600 hover:opacity-90 transition-opacity">
                Create Your First Guide
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => (
  <Card className="p-8 bg-background/70 backdrop-blur-lg border border-border shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 animate-fade-in group">
    <div className="space-y-4">
      <div className="p-3 bg-white rounded-lg shadow-md inline-block group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <h3 className="text-xl font-semibold">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  </Card>
);

const StepCard = ({ number, title, description }: { number: string; title: string; description: string }) => (
  <div className="p-8 space-y-4 animate-fade-in bg-background/70 backdrop-blur-lg rounded-xl border border-border shadow-lg">
    <div className="text-5xl font-bold bg-gradient-to-r from-primary to-purple-600 text-transparent bg-clip-text">{number}</div>
    <h3 className="text-xl font-semibold">{title}</h3>
    <p className="text-muted-foreground">{description}</p>
  </div>
);

const StatCard = ({ number, label }: { number: string; label: string }) => (
  <div className="text-center p-6 animate-fade-in">
    <div className="text-4xl font-bold bg-gradient-to-r from-primary to-purple-600 text-transparent bg-clip-text mb-2">
      {number}
    </div>
    <div className="text-muted-foreground">{label}</div>
  </div>
);

export default Index;
